import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  getDoc
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { sendEmail } from "../components/sendemail";

const ADMIN_UID = "7jWZ7mwnLKTw50d17XuqgQlZGbu1";

const safe = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (Array.isArray(val)) return val.map(v => safe(v)).join(", ");
  if (typeof val === "object") {
    try { return JSON.stringify(val); } catch { return ""; }
  }
  return String(val);
};

const formatAvailability = (availability) => {
  if (!availability) return "—";
  if (typeof availability === "string") return availability;
  if (typeof availability === "object" && !Array.isArray(availability)) {
    const entries = Object.entries(availability).filter(([, times]) => times && times.length > 0);
    if (entries.length === 0) return "—";
    return entries
      .map(([day, times]) => `${day}: ${Array.isArray(times) ? times.join(", ") : safe(times)}`)
      .join(" | ");
  }
  return safe(availability);
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [waitlist, setWaitlist] = useState([]);
  const [tab, setTab] = useState("pending");
  const [pendingTutors, setPendingTutors] = useState([]);
  const [approvedTutors, setApprovedTutors] = useState([]);
  const [requests, setRequests] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedTutors, setSelectedTutors] = useState({});
  const [totalRaised, setTotalRaised] = useState(800);
  const [raisedInput, setRaisedInput] = useState(800);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setLoading(false);
        navigate("/login");
        return;
      }
      if (user.uid !== ADMIN_UID) {
        setLoading(false);
        navigate("/login");
        return;
      }
      setAuthed(true);
      setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!authed) return;
    fetchData();
  }, [authed]);

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const settingsSnap = await getDoc(doc(db, "settings", "general"));
      if (settingsSnap.exists()) {
        setTotalRaised(settingsSnap.data().totalRaised || 800);
        setRaisedInput(settingsSnap.data().totalRaised || 800);
      }

      const usersSnap = await getDocs(collection(db, "users"));
      const allTutors = usersSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.role === "tutor");

      setPendingTutors(allTutors.filter(t => t.status === "pending" || !t.status));
      setApprovedTutors(allTutors.filter(t => t.status === "approved"));
      const waitlistSnap = await getDocs(collection(db, "waitlist"));
      setWaitlist(waitlistSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const reqSnap = await getDocs(collection(db, "requests"));
      setRequests(reqSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching data:", err);
    }
    setDataLoading(false);
  };

  const updateTotalRaised = async () => {
    await updateDoc(doc(db, "settings", "general"), {
      totalRaised: Number(raisedInput)
    });
    setTotalRaised(Number(raisedInput));
    alert("Total raised updated!");
  };

  const assignTutor = async (student) => {
    const tutorId = selectedTutors[student.id];
    if (!tutorId) {
      alert("Please select a tutor");
      return;
    }
    try {
      await addDoc(collection(db, "requests"), {
        tutorId,
        name: student.name,
        grade: student.grade,
        subjects: student.subjects,
        availability: student.availability,
        notes: student.notes,
        accepted: false,
        createdAt: new Date()
      });
      await deleteDoc(doc(db, "waitlist", student.id));
      setWaitlist(prev => prev.filter(s => s.id !== student.id));
      alert("Student assigned to tutor!");
    } catch (err) {
      console.error(err);
      alert("Error assigning tutor");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const approveTutor = async (tutorId) => {
    await updateDoc(doc(db, "users", tutorId), { status: "approved" });
    const tutor = pendingTutors.find(t => t.id === tutorId);
    await sendEmail(
      tutor.email,
      "Welcome to the SOWS Tutoring Team!",
      `<p>Hello ${tutor.name}!</p>
      <p>Thank you once again for your interest in joining the SOWS Fund tutoring program as a Tutor. After reviewing your application, we found that you would be a great addition to the SOWS team. We're excited to welcome you to an organization dedicated to supporting our local communities while helping Gazan students pursue higher education around the world. Your service will go a long way!</p>
      <p>Our goal is simple: "Supporting Education through Education"</p>
      <p>Here's how it works:</p>
      <ol>
        <li>Student and Tutor independently express areas of interest</li>
        <li>Student and Tutor are paired</li>
        <li>Student and Tutor meet</li>
        <li>Student and Tutor fill out <a href="https://docs.google.com/forms/d/e/1FAIpQLSe9FQqN8ldbDy9sHy13Wd_R8di6pNTBSjJDbHHCK4Ydlz3JJg/viewform?usp=header">Feedback</a> and <a href="https://docs.google.com/forms/d/e/1FAIpQLSdv0x8pqHQ-UpfIXUtwB2_SPslCOx9u0M-VExUAgmyfWOSIBA/viewform?usp=header">In-Kind</a> form, respectively</li>
        <li><a href="https://leonardeducation.org/the-seeds-of-a-watermelon-scholarship-fund/">SOWS Fund</a> gets funded!</li>
      </ol>
      <p>Your profile will now be featured on our SOWS website, where students can review and select you as a tutor based on their academic goals and needs. Please keep an eye out for tutoring requests from students.</p>
      <p>In the meantime, we ask that you review the <a href="https://docs.google.com/document/u/4/d/1z3elUTr95FreqlxeMDVs8GD4QrztJ3W9bTc7hPBh1Kg/edit">Tutoring Program: To Do</a> and join our <a href="https://chat.whatsapp.com/IcVyNAkZdzDB4WltxReC3n?mode=gi_t">Tutor Chat</a> to stay updated on program announcements and important information related to your role as a tutor.</p>
      <p>Please feel free to share any questions, comments, or concerns with us at <a href="mailto:sowstutoring@leonardeducation.org">sowstutoring@leonardeducation.org</a>.</p>
      <p>With Gratitude,<br/>SOWS</p>`
    );
    setPendingTutors(prev => prev.filter(t => t.id !== tutorId));
    setApprovedTutors(prev => [...prev, { ...tutor, status: "approved" }]);
  };

  const rejectTutor = async (tutorId) => {
    if (!window.confirm("Reject and delete this tutor application?")) return;
    await deleteDoc(doc(db, "users", tutorId));
    setPendingTutors(prev => prev.filter(t => t.id !== tutorId));
  };

  const unapproveTutor = async (tutorId) => {
    if (!window.confirm("Move this tutor back to pending?")) return;
    await updateDoc(doc(db, "users", tutorId), { status: "pending" });
    const tutor = approvedTutors.find(t => t.id === tutorId);
    setApprovedTutors(prev => prev.filter(t => t.id !== tutorId));
    setPendingTutors(prev => [...prev, { ...tutor, status: "pending" }]);
  };

  const removeFromWaitlist = async (id) => {
    if (!window.confirm("Remove this student from the waitlist?")) return;
    await deleteDoc(doc(db, "waitlist", id));
    setWaitlist(prev => prev.filter(s => s.id !== id));
  };

  const getRequestsForTutor = (tutorId) =>
    requests.filter(r => r.tutorId === tutorId);

  const acceptedCount = requests.filter(r => r.accepted === true).length;

  if (loading) return null;
  if (!authed) return null;

  return (
    <div className="admin-wrap">

      {/* HEADER */}
      <div className="admin-header">
        <div>
          <span className="admin-badge">ADMIN</span>
          <h1>Admin Dashboard</h1>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            className="admin-logout-btn"
            onClick={() => window.open("/dashboard", "_blank")}
          >
            View Sample Dashboard
          </button>
          <button className="admin-logout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </div>

      {/* TOTAL RAISED EDITOR */}
      <div style={{
        background: "#f6f0dc", borderRadius: "20px",
        padding: "24px", marginBottom: "28px",
        display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap"
      }}>
        <strong style={{ fontSize: "18px" }}>Total Raised ($):</strong>
        <input
          type="number"
          value={raisedInput}
          onChange={e => setRaisedInput(e.target.value)}
          style={{
            padding: "10px 16px", borderRadius: "12px",
            border: "1px solid #ccc", fontSize: "17px", width: "140px"
          }}
        />
        <button className="admin-approve-btn" onClick={updateTotalRaised}>
          Update
        </button>
        <span style={{ color: "#555" }}>Currently showing: <strong>${totalRaised}</strong> in emails</span>
      </div>

      {/* STATS */}
      <div className="admin-stats">
        <div className="admin-stat-card">
          <span className="admin-stat-num pending-color">{pendingTutors.length}</span>
          <span>Pending Approval</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-num approved-color">{approvedTutors.length}</span>
          <span>Active Tutors</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-num">{waitlist.length}</span>
          <span>Students on Waitlist</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-num">{acceptedCount}</span>
          <span>Accepted Matches</span>
        </div>
      </div>

      {/* TABS */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${tab === "pending" ? "active" : ""}`}
          onClick={() => setTab("pending")}
        >
          Pending Tutors
          {pendingTutors.length > 0 && (
            <span className="admin-tab-badge">{pendingTutors.length}</span>
          )}
        </button>
        <button
          className={`admin-tab ${tab === "tutors" ? "active" : ""}`}
          onClick={() => setTab("tutors")}
        >
          Active Tutors &amp; Students
        </button>
        <button
          className={`admin-tab ${tab === "waitlist" ? "active" : ""}`}
          onClick={() => setTab("waitlist")}
        >
          Waitlist
          {waitlist.length > 0 && (
            <span className="admin-tab-badge">{waitlist.length}</span>
          )}
        </button>
      </div>

      {dataLoading && <p className="admin-loading">Loading data…</p>}

      {/* PENDING TAB */}
      {tab === "pending" && !dataLoading && (
        <div className="admin-section">
          {pendingTutors.length === 0 ? (
            <div className="admin-empty">
              <p>✅ No pending tutor applications.</p>
            </div>
          ) : (
            pendingTutors.map(tutor => (
              <div key={tutor.id} className="admin-tutor-card">
                <div className="admin-tutor-top">
                  <div>
                    <h2>{safe(tutor.name)}</h2>
                    <p className="admin-tutor-email">{safe(tutor.email)}</p>
                    <span className="admin-status-badge pending">Pending Review</span>
                  </div>
                  <div className="admin-action-btns">
                    <button className="admin-approve-btn" onClick={() => approveTutor(tutor.id)}>
                      ✓ Approve
                    </button>
                    <button className="admin-reject-btn" onClick={() => rejectTutor(tutor.id)}>
                      ✕ Reject
                    </button>
                  </div>
                </div>
                <div className="admin-tutor-details">
                  <div className="admin-detail-row"><strong>School Year:</strong> {safe(tutor.schoolYear)}</div>
                  <div className="admin-detail-row"><strong>Subjects:</strong> {safe(tutor.subjects)}</div>
                  <div className="admin-detail-row"><strong>Max Students:</strong> {safe(tutor.maxStudents)}</div>
                  <div className="admin-detail-row"><strong>Bio:</strong> {safe(tutor.bio)}</div>
                  <div className="admin-detail-row"><strong>Availability:</strong> {formatAvailability(tutor.availability)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ACTIVE TUTORS TAB */}
      {tab === "tutors" && !dataLoading && (
        <div className="admin-section">
          {approvedTutors.length === 0 ? (
            <div className="admin-empty">
              <p>No approved tutors yet.</p>
            </div>
          ) : (
            approvedTutors.map(tutor => {
              const tutorRequests = getRequestsForTutor(tutor.id);
              const accepted = tutorRequests.filter(r => r.accepted === true);
              const pending = tutorRequests.filter(r => !r.accepted);

              return (
                <div key={tutor.id} className="admin-tutor-card">
                  <div className="admin-tutor-top">
                    <div>
                      <h2>{safe(tutor.name)}</h2>
                      <p className="admin-tutor-email">{safe(tutor.email)}</p>
                      <div style={{ display: "flex", gap: "8px", marginTop: "6px", flexWrap: "wrap" }}>
                        <span className="admin-status-badge approved">Approved</span>
                        {tutor.atCapacity === true && (
                          <span className="admin-status-badge at-cap">At Capacity</span>
                        )}
                      </div>
                    </div>
                    <div className="admin-action-btns">
                      <button className="admin-reject-btn" onClick={() => unapproveTutor(tutor.id)}>
                        Unapprove
                      </button>
                    </div>
                  </div>

                  <div className="admin-tutor-details">
                    <div className="admin-detail-row"><strong>Subjects:</strong> {safe(tutor.subjects)}</div>
                    <div className="admin-detail-row">
                      <strong>Students:</strong> {safe(tutor.currentStudents) || "0"} / {safe(tutor.maxStudents)}
                    </div>
                    <div className="admin-detail-row">
                      <strong>Total Hours Logged:</strong> {safe(tutor.totalHours) || "0"}
                    </div>
                  </div>

                  {accepted.length > 0 && (
                    <div className="admin-students-section">
                      <h4>Current Students ({accepted.length})</h4>
                      <div className="admin-student-grid">
                        {accepted.map(r => (
                          <div key={r.id} className="admin-student-card">
                            <div className="admin-student-name">{safe(r.name)}</div>
                            <div className="admin-student-detail">Grade: {safe(r.grade)}</div>
                            <div className="admin-student-detail">Subjects: {safe(r.subjects)}</div>
                            <div className="admin-student-detail">Times: {formatAvailability(r.availability)}</div>
                            {r.notes ? <div className="admin-student-notes">"{safe(r.notes)}"</div> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {pending.length > 0 && (
                    <div className="admin-students-section">
                      <h4 style={{ color: "#b07a00" }}>Pending Requests ({pending.length})</h4>
                      <div className="admin-student-grid">
                        {pending.map(r => (
                          <div key={r.id} className="admin-student-card pending-req">
                            <div className="admin-student-name">{safe(r.name)}</div>
                            <div className="admin-student-detail">Grade: {safe(r.grade)}</div>
                            <div className="admin-student-detail">Subjects: {safe(r.subjects)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {tutorRequests.length === 0 && (
                    <p className="admin-no-students">No student requests yet.</p>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* WAITLIST TAB */}
      {tab === "waitlist" && !dataLoading && (
        <div className="admin-section">
          {waitlist.length === 0 ? (
            <div className="admin-empty">
              <p>🎉 No students on the waitlist.</p>
            </div>
          ) : (
            <div className="admin-student-grid">
              {waitlist.map(student => (
                <div key={student.id} className="admin-student-card pending-req" style={{ position: "relative" }}>
                  <button
                    onClick={() => removeFromWaitlist(student.id)}
                    style={{
                      position: "absolute", top: "10px", right: "10px",
                      background: "none", border: "none", color: "#e57386",
                      fontSize: "22px", fontWeight: "bold", cursor: "pointer",
                      lineHeight: 1, padding: "2px 6px"
                    }}
                  >
                    ×
                  </button>
                  <div className="admin-student-name">{safe(student.name)}</div>
                  <div className="admin-student-detail">Grade: {safe(student.grade)}</div>
                  <div className="admin-student-detail">Subjects: {safe(student.subjects)}</div>
                  <div className="admin-student-detail">Availability: {safe(student.availability)}</div>
                  {student.notes && (
                    <div className="admin-student-notes">"{safe(student.notes)}"</div>
                  )}
                  <div className="admin-student-actions">
                    <select
                      value={selectedTutors[student.id] || ""}
                      onChange={(e) =>
                        setSelectedTutors(prev => ({
                          ...prev,
                          [student.id]: e.target.value
                        }))
                      }
                    >
                      <option value="">Select Tutor</option>
                      {approvedTutors.map(tutor => (
                        <option key={tutor.id} value={tutor.id}>
                          {tutor.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    className="admin-approve-btn"
                    onClick={() => assignTutor(student)}
                  >
                    Assign Tutor
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}